<template>
  <div>
    <div
      v-if="orderData"
      class="row"
    >
      <div class="col-xl-5 col-lg-12">
        <div class="card">
          <div class="card-header">
            <div class="cui__utils__heading mb-0">
              {{ $t('orders.order') + ' : ' + orderData.long_id }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.timestamp') }}:
                  </h6>
                  <p>{{ orderData.date_created | dateFormatter('YYYY-MM-DD HH:mm') }}</p>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.customer') }}:
                  </h6>
                  <p v-if="orderData.buyer">
                    {{ orderData.buyer.name }}
                    <a href="#"><i class="fa fa-paper-plane ml-2" /></a>
                  </p>
                </div>
              </div>
              <div class="col-lg-12">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.delivery_address') }}:
                  </h6>
                  <p>{{ orderData.delivery_address }}</p>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.phone') }}:
                  </h6>
                  <p v-if="orderData.buyer">
                    <a href="#">{{ orderData.buyer.phone | phoneFormatter }}</a>
                  </p>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.status') }}:
                  </h6>
                  <OrderStatusTag
                    :text="$l(orderData.status.title)"
                    :status="status"
                  />
                </div>
              </div>
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.product_cost') }}:
                  </h6>
                  <p>{{ orderData.amount - orderData.buyer_commission_cost - orderData.delivery_cost | money }} </p>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.payment') }}:
                  </h6>
                  <b-badge
                    v-if="orderData.payment_method.toUpperCase() === 'ONLINE'"
                    variant="success"
                  >
                    <i class="fe fe-check-circle mr-1" />{{ $t('orders.payment_type.' + orderData.payment_method.toLowerCase()) }}
                  </b-badge>
                  <b-badge
                    v-else
                    variant="warning"
                  >
                    <i class="fe fe-clock mr-1" />{{ $t('orders.payment_type.' + orderData.payment_method.toLowerCase()) }}
                  </b-badge>
                </div>
              </div>
              <div
                v-if="orderData.payment_method.toUpperCase() === 'ON_PREMISE'"
                class="col-lg-6"
              >
                <div class="mb-4">
                  <h6 class="mt-0">
                    {{ $t('orders.buyer_commission_cost') }}:
                  </h6>
                  <p>{{ orderData.buyer_commission_cost | money }}</p>
                </div>
              </div>
            </div>
            <div
              v-if="user && user.permissions.includes('SELLER_ORDERS_EDIT')"
              class="d-flex"
            >
              <b-button
                v-if="status === statusCodes.NEW"
                block
                variant="success"
                class="mr-3 mt-2"
                @click="accept"
              >
                <a-icon
                  v-if="loading"
                  type="loading"
                />
                <i
                  v-else
                  class="fe fe-check mr-2"
                />{{ ' ' + $t('common.confirm') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.NEW"
                block
                variant="danger"
                class="mr-3 mt-2"
                @click="showDecline=true"
              >
                <i class="fe fe-slash mr-2" />{{ ' ' + $t('orders.actions.decline') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.IN_PROGRESS && deliveryMethod === 'OTHER' "
                block
                variant="primary"
                class="mr-3 mt-2"
                @click="showDispatch = true"
              >
                <i class="fe fe-truck mr-2" />{{ ' ' + $t('orders.actions.dispatched') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.IN_PROGRESS && deliveryMethod === 'POST'"
                v-print="'#waybill-print'"
                block
                variant="primary"
                class="mr-3 mt-2"
              >
                <i class="fe fe-truck mr-2" />{{ ' ' + $t('orders.actions.download_waybill') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.DISPATCHED && deliveryMethod !== 'POST'"
                block
                variant="warning"
                class="mr-3 mt-2"
                @click="confirmDelivery"
              >
                <a-icon
                  v-if="loading"
                  type="loading"
                />
                <i
                  v-else
                  class="fe fe-check-square mr-2"
                />{{ ' ' + $t('orders.actions.delivered') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.REJECTED"
                block
                variant="primary"
                class="mr-3 mt-2"
                @click="acceptReturn"
              >
                <i class="fe fe-check mr-2" />{{ ' ' + $t('orders.actions.accept_return') }}
              </b-button>
              <b-button
                v-if="status === statusCodes.REJECTED"
                block
                variant="warning"
                class="mr-3 mt-2"
                @click="declineReturn"
              >
                <i class="fe fe-alert-octagon mr-2" />{{ ' ' + $t('orders.actions.decline_return') }}
              </b-button>
            </div>
          </div>
        </div>
        <b-modal
          v-model="showDecline"
          :busy="loading"
          centered
          :title="$t('orders.actions.decline')"
          @ok="decline"
        >
          <a-radio-group
            v-model="declineReasonOption"
            @change="onDeclineReasonOptionChange"
          >
            <a-radio
              :style="radioStyle"
              :value="1"
            >
              {{ $t('orders.decline_reason_options.unavailable') }}
            </a-radio>
            <a-radio
              :style="radioStyle"
              :value="2"
            >
              {{ $t('orders.decline_reason_options.price_change') }}
            </a-radio>
            <a-radio
              :style="radioStyle"
              :value="3"
            >
              {{ $t('orders.decline_reason_options.other') }}
              <a-input
                v-if="declineReasonOption === 3"
                v-model="declineReason"
                :placeholder="$t('orders.decline_reason')"
                :style="{ width: 100, marginLeft: 10 }"
              />
            </a-radio>
          </a-radio-group>
        </b-modal>
        <b-modal
          v-model="showDispatch"
          :busy="loading"
          centered
          :title="$t('orders.actions.dispatched_track')"
          @ok="dispatch"
        >
          <b-input
            v-model="dispatchComment"
            :placeholder="$t('orders.comment')"
            class="mb-3"
          />
          <b-input
            v-if="deliveryMethod === 'POST'"
            v-model="dispatchTrackingNumber"
            :placeholder="$t('orders.tracking_number')"
          />
        </b-modal>
        <b-modal
          v-model="showDeclineReturn"
          :busy="loading"
          centered
          :title="$t('orders.actions.decline_return')"
          @ok="declineReturn"
        >
          <b-textarea
            v-model="declineReturnReason"
            :placeholder="$t('orders.decline_reason')"
          />
        </b-modal>
      </div>
      <b-col
        xl="3"
        lg="6"
      >
        <div class="card">
          <div class="card-header">
            <div class="cui__utils__heading mb-0">
              {{ $t('orders.change_log') }}
            </div>
          </div>
          <div class="card-body">
            <OrderChangeLog :log="statusLog" />
          </div>
        </div>
      </b-col>
      <div class="col-xl-4 col-lg-6">
        <div class="card">
          <div class="card-header">
            <div class="cui__utils__heading mb-0">
              {{ $t('orders.notes.order_notes') }}
            </div>
          </div>
          <div class="card-body">
            <OrderNotes
              :notes="notes"
              @input="postNote"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="orderData"
      class="card"
    >
      <div class="card-header">
        <div class="cui__utils__heading mb-0">
          {{ $t('product.self_multiple') }}
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-bordered table-centered mb-0">
            <thead class="thead-light">
              <tr>
                <th>{{ $t('product.self') }}</th>
                <th>{{ $t('product.code') }}</th>
                <th>{{ $t('product.attributes') }}</th>
                <th>{{ $t('orders.quantity') }}</th>
                <th>{{ $t('product.price') }}</th>
                <th>{{ $t('orders.total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in orderData.detailed_items"
                :key="'order_item_' + index"
              >
                <th>
                  <router-link :to="'/products/' + item.id + '?isCompanyProduct=true'">
                    {{ $l(item.title) }}
                  </router-link>
                </th>
                <td>{{ $l(item.code) }}</td>
                <td>
                  <p
                    v-for="(attr, j) in item.attributes"
                    :key="index + '_item_attribute_' + j"
                    class="col-md-12"
                  >
                    {{ $l(attr.title) + ': ' + $l(attr.value) }}
                  </p>
                </td>
                <td>{{ item.count }}</td>
                <td>{{ item.price_per_item | currency }}<small>UZS</small></td>
                <td>{{ item.total_price | currency }}<small>UZS</small></td>
              </tr>
              <tr>
                <th
                  scope="row"
                  colspan="5"
                  class="text-right"
                >
                  {{ $t('orders.subtotal') }}:
                </th>
                <td>
                  <div class="font-weight-bold">
                    {{ (orderData.amount - orderData.buyer_commission_cost - orderData.delivery_cost) | currency }}<small>UZS</small>
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  scope="row"
                  colspan="5"
                  class="text-right"
                >
                  {{ $t('orders.buyer_commission_cost') }}:
                </th>
                <td>
                  <div class="font-weight-bold">
                    {{ orderData.buyer_commission_cost | currency }}<small>UZS</small>
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  scope="row"
                  colspan="5"
                  class="text-right"
                >
                  {{ $t('orders.total') }}:
                </th>
                <td>
                  <div class="font-weight-bold">
                    {{ orderData.amount | currency }}<small>UZS</small>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-show="false">
      <OrderWaybill
        v-show="true"
        :data="waybill"
      />
    </div>
  </div>
</template>
<script>
import orderService from '@/services/api/manager/order-service'
import OrderStatusTag from '@/components/order/OrderStatusTag'
import OrderChangeLog from '@/components/order/OrderChangeLog'
import OrderNotes from '@/components/order/OrderNotes'
import OrderWaybill from '@/components/order/OrderWaybill'
import print from 'vue-print-nb'
import { mapGetters } from 'vuex'

export default {
  name: 'OrderDetails',
  components: {
    OrderWaybill,
    OrderNotes,
    OrderChangeLog,
    OrderStatusTag,
  },
  data () {
    return {
      orderId: this.$route.params.id,
      orderData: null,
      statusCodes,
      notes: [],
      statusLog: [],
      showDecline: false,
      declineReason: 'Товара нет в наличии / Mahsulot mavjud emas',
      declineReasonOption: 1,
      showReject: false,
      showDispatch: false,
      dispatchComment: '',
      dispatchTrackingNumber: '',
      showDeclineReturn: false,
      declineReturnReason: '',
      loading: false,
      waybill: null,
      waybillPrint: {
        id: 'waybill-print',
        popTitle: 'Заказ',
        extraHead: '<meta charset="UTF-8">',
      },
      radioStyle: {
        display: 'block',
        height: '30px',
        lineHeight: '30px',
      },
    }
  },
  computed: {
    status () {
      return this.orderData?.status?.code ?? null
    },
    deliveryMethod () {
      const method = this.orderData?.delivery_type ?? ''
      if (method.startsWith('POST')) return 'POST'
      else if (method.startsWith('OTHER') || method.startsWith('SELF')) return 'OTHER'
      return method
    },
    ...mapGetters({
      user: 'user/user',
    }),
  },
  mounted () {
    this.fetchOrderInfo()
    this.fetchNotes()
    this.fetchStatusLog()
    this.fetchWaybill()
  },
  methods: {
    async fetchOrderInfo () {
      const { data } = await orderService.getOrder(this.orderId)
      this.orderData = data?.content
    },
    async fetchNotes () {
      const { data } = await orderService.fetchNotes(this.orderId)
      this.notes = data?.content ?? []
    },
    async fetchStatusLog () {
      const { data } = await orderService.fetchStatusHistory(this.orderId)
      this.statusLog = data?.content ?? []
    },
    accept () {
      this.loading = true
      orderService.acceptOrder(this.orderId)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
          this.fetchWaybill()
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    decline () {
      this.loading = true
      orderService.declineOrder(this.orderId, this.declineReason)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
          this.showDecline = false
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    dispatch () {
      this.loading = true
      orderService.dispatchOrder(this.orderId, this.dispatchComment, this.dispatchTrackingNumber)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
          this.showDispatch = false
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    async fetchWaybill () {
      const { data } = await orderService.getWaybill(this.orderId).catch(() => null)
      this.waybill = data.content
    },
    confirmDelivery () {
      this.loading = true
      orderService.confirmOrderDelivery(this.orderId)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    acceptReturn () {
      this.loading = true
      orderService.acceptOrderReturn(this.orderId)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    declineReturn () {
      this.loading = true
      orderService.declineOrderReturn(this.orderId, this.declineReturnReason)
        .then((response) => {
          this.orderData.status = response?.data?.content?.status ?? this.orderData.status
        })
        .finally(() => {
          this.loading = false
          this.fetchStatusLog()
        })
    },
    async postNote (text) {
      await orderService.postNote(this.orderId, text)
      this.fetchNotes()
    },
    onDeclineReasonOptionChange(e) {
      console.log('radio checked', e.target.value)
      switch (this.declineReasonOption) {
        case 1: this.declineReason = 'Товара нет в наличии / Mahsulot mavjud emas'; break
        case 2: this.declineReason = "Стоимость товара изменена / Mahsulot narxi o'zgardi"; break
        case 3: this.declineReason = ''; break
      }
      console.log(this.declineReason)
    },
  },
  directives: {
    print,
  },
}

const statusCodes = {
  NEW: 'NEW',
  CANCELLED: 'CANCELLED',
  IN_PROGRESS: 'IN_PROGRESS',
  DISPATCHED: 'DISPATCHED',
  DELIVERED: 'DELIVERED',
  FINISHED: 'FINISHED',
  REJECTED: 'REJECTED',
  REFUNDED: 'REFUNDED',
}
</script>
<style scoped>
.custom-filter-dropdown {
  padding: 8px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.highlight {
  background-color: rgb(255, 192, 105);
  padding: 0px;
}
</style>
